import {Link} from "react-router-dom";
import {PhoneCall, MessageCircleMore, PenTool, Footprints, Users} from "lucide-react";
import type {CustomerListItem} from "~/types/customer-detail.types";
import {useHostessStore} from "@/stores/hostess";
import {useCreateActivity, type ActivityType} from "@/hooks/useActivities";
import {toast} from "@sakura-grp/ui/sonner";
import {triggerHaptic} from "~/lib/haptics";
import {CUSTOMER_TYPE_LABEL} from "@sakura-grp/lib";

interface BaseTabContentProps {
  customers: CustomerListItem[];
  emptyMessage?: string;
  emptyDescription?: string;
}

/** 空状態コンポーネント */
const EmptyState = ({
  message = "顧客が見つかりません",
  description = "条件に一致する顧客がいません。",
}: {
  message?: string;
  description?: string;
}) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="flex size-16 items-center justify-center rounded-full bg-gray-100 mb-4">
      <Users className="size-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-2">{message}</h3>
    <p className="text-sm text-gray-500 text-center">{description}</p>
  </div>
);

export const BaseTabContent = ({
  customers,
  emptyMessage,
  emptyDescription,
}: BaseTabContentProps) => {
  if (customers.length === 0) {
    return <EmptyState message={emptyMessage} description={emptyDescription} />;
  }

  return (
    <ul className="divide-y divide-stone-200">
      {customers.map((customer) => (
        <CustomerListItem key={customer.id} customer={customer} />
      ))}
    </ul>
  );
};

interface CustomerListItemProps {
  customer: CustomerListItem;
}

const CustomerListItem = ({customer}: CustomerListItemProps) => {
  const {hostess} = useHostessStore();
  const createActivity = useCreateActivity();

  const {
    id,
    registeredDate,
    storeName,
    storeId,
    hostessName,
    statusType,
    statusLabel,
    activities,
    restrictions,
    emoji,
    points,
    name,
    company,
    linkTo,
    type,
    isMyAssigned,
    isBranch,
  } = customer;

  // 営業活動を記録するハンドラー
  const handleActivityClick = (activityType: ActivityType) => {
    // UUIDかどうかをチェック（モックデータはnumber、実データはUUID string）
    const isRealData = typeof id === "string" && id.includes("-");

    if (!isRealData) {
      toast.info("デモモード: 営業活動の記録はスキップされます", {
        duration: 2000,
      });
      return;
    }

    if (!hostess) {
      toast.error("ログイン情報を取得できませんでした", {
        duration: 3000,
      });
      return;
    }

    if (!storeId) {
      toast.error("店舗情報がありません", {
        duration: 3000,
      });
      return;
    }

    triggerHaptic("light");
    createActivity.mutate({
      store_id: storeId,
      customer_id: id as string,
      hostess_id: hostess.id,
      activity_type: activityType,
    });
  };

  const statusColorClass =
    statusType === "poor"
      ? "bg-red-400 text-red-500"
      : "bg-success text-success";

  const baseLabel = CUSTOMER_TYPE_LABEL[type] ?? "非会員";
  const otherSuffix = type === 'assigned' && !isMyAssigned ? '[他]' : '';
  const branchSuffix = isBranch ? '[枝]' : '';
  const categoryLabel = `${baseLabel}${otherSuffix}${branchSuffix}`;

  return (
    <li className="gap-sm px-lg h-(--h-list) relative flex flex-col justify-center">
      <div className="gap-md flex items-center">
        <time className="text-sm tracking-normal">{registeredDate}</time>
        <strong className="py-sm bg-secondary text-primary text-2xs absolute end-0 top-0 rounded-bl-lg border-b border-s px-[.7em] font-bold leading-snug tracking-tight">
          {hostessName ? `${storeName} / ${hostessName}` : storeName}
        </strong>
        <span className="text-sm">{categoryLabel}</span>
        <span className="gap-xs flex items-center justify-center">
          <span
            className={`inline-block size-[1em] rounded-full ${statusColorClass.split(" ")[0]}`}
          ></span>
          <span className={`text-xs font-bold ${statusColorClass.split(" ")[1]}`}>
            {statusLabel}
          </span>
        </span>
        <ul className="gap-sm flex text-sm">
          {activities.phone !== undefined && (
            <li>
              <PhoneCall className="me-xs size-(--bigger-md) text-success" />
              {activities.phone}
            </li>
          )}
          {activities.message !== undefined && (
            <li>
              <MessageCircleMore className="me-xs size-(--bigger-md) text-success" />
              {activities.message}
            </li>
          )}
          {activities.note !== undefined && (
            <li>
              <PenTool className="me-xs size-(--bigger-md) text-success" />
              {activities.note}
            </li>
          )}
          {activities.visit !== undefined && (
            <li>
              <Footprints className="me-xs size-(--bigger-md) text-success" />
              {activities.visit}
            </li>
          )}
        </ul>
      </div>
      <div className="flex items-center justify-between">
        <div className="gap-sm flex items-center">
          <div
            className={`float-none w-[2.45rem] text-center leading-none ${
              points
                ? "space-y-xs text-[2rem]"
                : "-translate-y-sm text-[2.45rem]"
            }`}
          >
            <div>{emoji}</div>
            {points && <div className="text-sm text-brand">{points}pt</div>}
          </div>
          <div className="space-y-sm">
            <Link to={linkTo} className="block">
              <h3 className="text-2xl leading-none text-gray-800">{name}</h3>
            </Link>
            {company && (
              <span className="block text-xs font-normal tracking-tight">
                ( {company} )
              </span>
            )}
          </div>
        </div>
        <div className="gap-md flex items-center">
          <button
            type="button"
            onClick={() => handleActivityClick("phone")}
            disabled={createActivity.isPending || !!restrictions?.phone}
            className="ms-auto block active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
          >
            <div className="flex size-[2.9rem] items-center justify-center rounded-full border border-gray-400">
              <PhoneCall className="size-[1.5rem]" />
            </div>
            {restrictions?.phone && (
              <span className="block text-[0.6rem] font-bold text-red-500 mt-0.5">NG</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleActivityClick("message")}
            disabled={createActivity.isPending || !!restrictions?.message}
            className="ms-auto block active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
          >
            <div className="flex size-[2.9rem] items-center justify-center rounded-full border border-gray-400">
              <MessageCircleMore className="size-[1.5rem]" />
            </div>
            {restrictions?.message && (
              <span className="block text-[0.6rem] font-bold text-red-500 mt-0.5">NG</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleActivityClick("letter")}
            disabled={createActivity.isPending || !!restrictions?.dm}
            className="ms-auto block active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
          >
            <div className="flex size-[2.9rem] items-center justify-center rounded-full border border-gray-400">
              <PenTool className="size-[1.5rem]" />
            </div>
            {restrictions?.dm && (
              <span className="block text-[0.6rem] font-bold text-red-500 mt-0.5">NG</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => handleActivityClick("visit")}
            disabled={createActivity.isPending || !!restrictions?.visit}
            className="ms-auto block active:scale-95 transition-transform disabled:opacity-40 disabled:active:scale-100"
          >
            <div className="flex size-[2.9rem] items-center justify-center rounded-full border border-gray-400">
              <Footprints className="size-[1.5rem]" />
            </div>
            {restrictions?.visit && (
              <span className="block text-[0.6rem] font-bold text-red-500 mt-0.5">NG</span>
            )}
          </button>
        </div>
      </div>
    </li>
  );
};
